import { ROUTES } from 'Utils/constants'
import { hasCustomerDisplayWindowPresence } from './customerDisplayPresence'

const CUSTOMER_DISPLAY_WINDOW_NAME = 'customer_display'
const CONFIG_STORAGE_KEYS = {
  left: 'customerDisplayLeft',
  top: 'customerDisplayTop',
  width: 'customerDisplayWidth',
  height: 'customerDisplayHeight',
  screenSignature: 'customerDisplayScreenSignature',
  updatedAt: 'customerDisplayConfigUpdatedAt',
}
const STORED_CONFIG_TTL_MS = 7 * 24 * 60 * 60 * 1000

const getCustomerDisplayWindowName = () => CUSTOMER_DISPLAY_WINDOW_NAME

export const getCustomerDisplayUrl = () =>
  `${globalThis.location.origin}${ROUTES.customerDisplay}`

export const hasCustomerDisplaySecondaryScreen = () =>
  globalThis.screen?.isExtended === true

const parseStoredNumber = key => {
  const value = Number(globalThis.localStorage?.getItem(key))

  return Number.isFinite(value) ? value : null
}

const getCurrentScreenSignature = () =>
  JSON.stringify({
    width: globalThis.screen.width,
    height: globalThis.screen.height,
    availWidth: globalThis.screen.availWidth,
    availHeight: globalThis.screen.availHeight,
    availLeft: globalThis.screen.availLeft ?? 0,
    availTop: globalThis.screen.availTop ?? 0,
    devicePixelRatio: globalThis.devicePixelRatio || 1,
  })

const isStoredConfigFresh = () => {
  const updatedAt = parseStoredNumber(CONFIG_STORAGE_KEYS.updatedAt)
  if (!updatedAt) return false

  return Date.now() - updatedAt <= STORED_CONFIG_TTL_MS
}

const getStoredConfig = () => {
  if (!hasCustomerDisplaySecondaryScreen()) return null

  const left = parseStoredNumber(CONFIG_STORAGE_KEYS.left)
  const top = parseStoredNumber(CONFIG_STORAGE_KEYS.top)
  const width = parseStoredNumber(CONFIG_STORAGE_KEYS.width)
  const height = parseStoredNumber(CONFIG_STORAGE_KEYS.height)
  const screenSignature = globalThis.localStorage?.getItem(
    CONFIG_STORAGE_KEYS.screenSignature
  )

  if ([left, top, width, height].some(value => value === null)) return null
  if (width <= 0 || height <= 0) return null
  if (screenSignature !== getCurrentScreenSignature()) return null
  if (!isStoredConfigFresh()) return null

  return { width, height, left, top }
}

// Safe fallback for manual activation without a secondary screen.
const getCurrentScreenConfig = () => ({
  width: globalThis.screen.availWidth || globalThis.screen.width,
  height: globalThis.screen.availHeight || globalThis.screen.height,
  left: globalThis.screen.availLeft ?? 0,
  top: globalThis.screen.availTop || 0,
})

const getScreenBounds = screen => ({
  left: Number(screen?.left ?? screen?.availLeft),
  top: Number(screen?.top ?? screen?.availTop),
  width: Number(screen?.width ?? screen?.availWidth),
  height: Number(screen?.height ?? screen?.availHeight),
})

const hasValidScreenBounds = ({ left, top, width, height }) =>
  [left, top, width, height].every(Number.isFinite) && width > 0 && height > 0

const screensHaveSameBounds = (firstScreen, secondScreen) => {
  const first = getScreenBounds(firstScreen)
  const second = getScreenBounds(secondScreen)

  return (
    hasValidScreenBounds(first) &&
    hasValidScreenBounds(second) &&
    first.left === second.left &&
    first.top === second.top &&
    first.width === second.width &&
    first.height === second.height
  )
}

const screenBoundsOverlap = (firstScreen, secondScreen) => {
  const first = getScreenBounds(firstScreen)
  const second = getScreenBounds(secondScreen)

  if (!hasValidScreenBounds(first) || !hasValidScreenBounds(second)) {
    return true
  }

  return (
    first.left < second.left + second.width &&
    first.left + first.width > second.left &&
    first.top < second.top + second.height &&
    first.top + first.height > second.top
  )
}

const getScreenConfig = screen => {
  const config = {
    width: Number(screen?.availWidth ?? screen?.width),
    height: Number(screen?.availHeight ?? screen?.height),
    left: Number(screen?.availLeft ?? screen?.left),
    top: Number(screen?.availTop ?? screen?.top),
  }

  return hasValidScreenBounds(config) ? config : null
}

// Uses the Window Management API to find the actual secondary screen position.
// Works for any monitor layout (left, right, above, below).
// Returns null if the API is unavailable or permission is denied.
const getSecondaryScreenConfig = async ({
  keepCurrentSecondary = false,
} = {}) => {
  if (!hasCustomerDisplaySecondaryScreen()) return null
  if (typeof globalThis.getScreenDetails !== 'function') return null

  try {
    const screenDetails = await globalThis.getScreenDetails()
    const { currentScreen, screens } = screenDetails

    if (!Array.isArray(screens) || screens.length < 2) {
      return null
    }

    const sourceScreen =
      currentScreen || screens.find(screen => screen.isPrimary)

    if (!sourceScreen) return null

    // When this runs inside the CD, "another screen" can be the operator's
    // primary monitor. Keep the current screen when it is already secondary.
    const secondary =
      keepCurrentSecondary && currentScreen?.isPrimary === false
        ? currentScreen
        : screens.find(
            screen =>
              !screensHaveSameBounds(screen, sourceScreen) &&
              screen.isPrimary === false
          ) ||
          screens.find(screen => !screensHaveSameBounds(screen, sourceScreen))
    if (!secondary) return null

    if (
      secondary !== sourceScreen &&
      (screensHaveSameBounds(secondary, sourceScreen) ||
        screenBoundsOverlap(secondary, sourceScreen))
    ) {
      console.warn(
        'La topología de pantallas aún no permite posicionar Customer Display.'
      )
      return null
    }

    return getScreenConfig(secondary)
  } catch (error) {
    console.warn('No se pudo obtener la pantalla secundaria:', error)
    return null
  }
}

export const prepareCustomerDisplayWindowConfig = () =>
  getSecondaryScreenConfig()

const hasKnownSecondaryScreen = screenDetails => {
  if (globalThis.screen?.isExtended === false) return false
  if (screenDetails?.screens?.length > 1) return true

  return globalThis.screen?.isExtended === true
}

const getInitialConfig = () => getStoredConfig() || getCurrentScreenConfig()

const buildWindowFeatures = ({ width, height, left, top }) =>
  [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'popup=yes',
    'toolbar=no',
    'menubar=no',
    'location=no',
    'status=no',
    'scrollbars=no',
    'resizable=yes',
  ].join(',')

const persistConfig = ({ width, height, left, top }) => {
  try {
    globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.left, left)
    globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.top, top)
    globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.width, width)
    globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.height, height)
    globalThis.localStorage?.setItem(
      CONFIG_STORAGE_KEYS.screenSignature,
      getCurrentScreenSignature()
    )
    globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.updatedAt, Date.now())
    return true
  } catch (error) {
    console.warn(
      'No se pudo guardar la posición de la ventana de Customer Display:',
      error
    )
    return false
  }
}

const applyConfig = (win, config, { shouldPersist = true } = {}) => {
  try {
    win.moveTo?.(config.left, config.top)
    win.resizeTo?.(config.width, config.height)
  } catch (error) {
    console.warn('No se pudo posicionar la ventana de Customer Display:', error)
    return false
  }

  if (shouldPersist) {
    persistConfig(config)
  }

  return true
}

// Repositions the window on the actual secondary screen once the async API responds.
// If the API is unavailable, the window stays at the stored or legacy position.
const repositionOnSecondaryScreen = async win => {
  try {
    const config = await getSecondaryScreenConfig()
    if (config && !win.closed) applyConfig(win, config)
  } catch (error) {
    console.warn(
      'No se pudo reposicionar la ventana de Customer Display:',
      error
    )
  }
}

export const positionCurrentCustomerDisplayWindow = async () => {
  const config = await getSecondaryScreenConfig({
    keepCurrentSecondary: true,
  })

  if (!config) return false

  return applyConfig(globalThis, config, { shouldPersist: false })
}

export const watchSecondaryScreenDisconnect = async onDisconnect => {
  let isDisposed = false
  let hadSecondaryScreen = false
  let didNotifyDisconnect = false
  let screenDetails = null

  const checkScreens = () => {
    if (isDisposed) return

    const hasSecondaryScreen = hasKnownSecondaryScreen(screenDetails)

    if (hasSecondaryScreen) {
      hadSecondaryScreen = true
      return
    }

    if (hadSecondaryScreen && !didNotifyDisconnect) {
      didNotifyDisconnect = true
      onDisconnect()
    }
  }

  const intervalId = globalThis.setInterval(checkScreens, 2000)

  if (typeof globalThis.getScreenDetails === 'function') {
    try {
      screenDetails = await globalThis.getScreenDetails()
      screenDetails.addEventListener?.('screenschange', checkScreens)
    } catch (error) {
      console.warn(
        'No se pudo observar cambios de pantallas para Customer Display:',
        error
      )
      screenDetails = null
    }
  }

  globalThis.addEventListener?.('focus', checkScreens)
  globalThis.addEventListener?.('resize', checkScreens)
  checkScreens()

  return () => {
    isDisposed = true
    globalThis.clearInterval(intervalId)
    screenDetails?.removeEventListener?.('screenschange', checkScreens)
    globalThis.removeEventListener?.('focus', checkScreens)
    globalThis.removeEventListener?.('resize', checkScreens)
  }
}

export const openCustomerDisplayWindow = ({
  url,
  skipPresenceCheck = false,
  windowConfig,
  windowName,
} = {}) => {
  if (!skipPresenceCheck && hasCustomerDisplayWindowPresence()) return null

  const config = windowConfig || getInitialConfig()
  let win = null

  try {
    win = globalThis.open(
      url === undefined ? getCustomerDisplayUrl() : url,
      windowName || getCustomerDisplayWindowName(),
      buildWindowFeatures(config)
    )
  } catch (error) {
    console.warn('No se pudo abrir la ventana de Customer Display:', error)
    return null
  }

  if (!win) return null

  applyConfig(win, config, { shouldPersist: Boolean(windowConfig) })

  if (!windowConfig) {
    repositionOnSecondaryScreen(win)
  }

  return win
}

export const isCustomerDisplayWindowHandle = customerDisplayWindow => {
  if (!customerDisplayWindow || customerDisplayWindow.closed) return false

  try {
    return customerDisplayWindow.location.pathname.startsWith(
      ROUTES.customerDisplay
    )
  } catch {
    return false
  }
}

export const closeCustomerDisplayWindowHandle = customerDisplayWindow => {
  if (!customerDisplayWindow || customerDisplayWindow.closed) return false

  try {
    customerDisplayWindow.close()
    return true
  } catch (error) {
    console.warn('No se pudo cerrar la ventana de Customer Display:', error)
    return false
  }
}

export const navigateCustomerDisplayWindow = (
  customerDisplayWindow,
  { skipPresenceCheck = false, windowConfig } = {}
) => {
  if (!skipPresenceCheck && hasCustomerDisplayWindowPresence()) return false

  if (!customerDisplayWindow || customerDisplayWindow.closed) {
    return Boolean(
      openCustomerDisplayWindow({ skipPresenceCheck, windowConfig })
    )
  }

  const config = windowConfig || getInitialConfig()
  applyConfig(customerDisplayWindow, config, { shouldPersist: false })

  if (!windowConfig) {
    repositionOnSecondaryScreen(customerDisplayWindow)
  }
  customerDisplayWindow.name = getCustomerDisplayWindowName()
  customerDisplayWindow.location.href = getCustomerDisplayUrl()

  return true
}
